import List from "@mui/material/List";
import Chip from "@mui/material/Chip";
import Input from "@mui/material/Input";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import * as api from "../../../shared/lib/api";
import ListItem from "@mui/material/ListItem";
import { useNavigate } from "react-router-dom";
import { useCore } from "../../../shared/hooks/use-core";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import { toast, ToastContainer } from "react-toastify";
import ListItemButton from "@mui/material/ListItemButton";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import { Paginator } from "../../../shared/components/paginator";
import ModalDelete from "../../../shared/components/modal-delete";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { ApiProps, ListAllApiProps } from "../../../features/api/types/api";
import {
	ListAllProductProps,
	ListProductProps,
	ProductProps,
} from "../../../features/product/types/product";
import { useDebounce } from "../../../shared/hooks/use-debounce";

export default function Products(): JSX.Element {
	const navigate = useNavigate();
	const { setPathTitleBar, setTitleBar } = useCore();

	const [products, setProducts] = useState<ProductProps[]>([]);
	const [open, setOpen] = useState<boolean>(false);
	const [idDelete, setIdDelete] = useState<string | undefined>();
	const [searchValue, setSearchValue] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [count, setCount] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const debouncedSearch = useDebounce(searchValue, 500);

	// Busca de insumo em combos
	const [insumoSearchValue, setInsumoSearchValue] = useState<string>("");
	const [insumos, setInsumos] = useState<ApiProps[]>([]);
	const [allProducts, setAllProducts] = useState<ProductProps[]>([]);
	const [insumoDataLoaded, setInsumoDataLoaded] = useState<boolean>(false);
	const [selectedInsumo, setSelectedInsumo] = useState<ApiProps | null>(null);
	const debouncedInsumoSearch = useDebounce(insumoSearchValue, 500);

	const buildListUrl = (page: number, termRaw: string) => {
		const base = `product/list/?page=${page}`;
		const term = (termRaw || "").trim();
		return term.length >= 2
			? `${base}&search=${encodeURIComponent(term)}`
			: base;
	};

	const getProducts = async (url: string) => {
		setLoading(true);
		const response = (await api.get(url)) as ListProductProps;
		setProducts(response.content.results);
		setCount(response.content.count);
		setLoading(false);
	};

	const handleChangePage = async (
		event: React.ChangeEvent<unknown>,
		value: number,
	) => {
		setLoading(true);
		setPageNumber(value);
		await getProducts(buildListUrl(value, debouncedSearch));
	};

	const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchValue(e.target.value);
	};

	function handleModalOpen(id: string) {
		setIdDelete(id);
		setOpen(true);
	}

	const handleChangeInsumoSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInsumoSearchValue(e.target.value);
		setSelectedInsumo(null);
	};

	useEffect(() => {
		const term = debouncedInsumoSearch.trim();
		if (term.length < 2 || insumoDataLoaded) return;

		setInsumoDataLoaded(true);
		Promise.all([
			api.get("api/list/all/") as Promise<ListAllApiProps>,
			api.get("product/list/all/") as Promise<ListAllProductProps>,
		]).then(([apisResponse, productsResponse]) => {
			setInsumos(apisResponse.content);
			setAllProducts(productsResponse.content);
		});
	}, [debouncedInsumoSearch, insumoDataLoaded]);

	const insumoSearchTerm = debouncedInsumoSearch.trim().toLowerCase();
	const matchingInsumos =
		insumoSearchTerm.length >= 2
			? insumos.filter(
					(insumo) =>
						insumo.name.toLowerCase().includes(insumoSearchTerm) ||
						insumo.id.toLowerCase().includes(insumoSearchTerm) ||
						insumo.provider_name.toLowerCase().includes(insumoSearchTerm),
				)
			: [];
	// TODO: perguntar ao PO se insumos/combos com is_active=false devem ficar de fora dessa busca
	const matchingProducts = selectedInsumo
		? allProducts.filter((product) => product.apis.includes(selectedInsumo.id))
		: [];

	useEffect(() => {
		const term = debouncedSearch.trim();
		if (term.length === 0) {
			setLoading(true);
			setPageNumber(1);
			getProducts(buildListUrl(1, ""));
		} else if (term.length >= 2) {
			setLoading(true);
			setPageNumber(1);
			getProducts(buildListUrl(1, term));
		}
	}, [debouncedSearch]);

	const handleModalClose = () => setOpen(false);

	const handleDelete = async () => {
		setLoading(true);
		const response = (await api.destroy(`product/destroy/${idDelete}/`)) as {
			[key: string]: any;
		};
		if (response.status === 204) {
			toast.success("Produto excluído com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});
			await getProducts(buildListUrl(pageNumber, debouncedSearch));
		} else {
			toast.error("Falha ao tentar excluir produto!", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
		handleModalClose();
		setLoading(false);
	};

	useEffect(() => {
		getProducts(buildListUrl(1, "")).catch(console.error);
	}, []);

	useLayoutEffect(() => {
		setTitleBar("Produtos");
		setPathTitleBar("/new-product");
	}, [setTitleBar, setPathTitleBar]);

	return (
		<>
			<ToastContainer />
			<ModalDelete
				open={open}
				textBody="Tem certeza que deseja excluir esse produto?"
				handleModalClose={handleModalClose}
				handleDelete={handleDelete}
				loading={loading}
			/>

			<Typography variant="h6">Buscar insumo em combos</Typography>
			<Typography
				variant="body2"
				color="text.secondary"
				sx={{ marginBottom: 2 }}
			>
				Pesquise um insumo (API) e veja em quais combos ele está configurado.
			</Typography>

			<FormControl fullWidth sx={{ marginBottom: 2 }} variant="standard">
				<InputLabel htmlFor="input-search-insumo">
					Buscar insumo (nome ou id)
				</InputLabel>
				<Input
					id="input-search-insumo"
					startAdornment={
						<InputAdornment position="start">
							<SearchIcon />
						</InputAdornment>
					}
					onChange={handleChangeInsumoSearch}
				/>
			</FormControl>

			{insumoSearchTerm.length >= 2 &&
				!selectedInsumo &&
				(matchingInsumos.length > 0 ? (
					<List dense disablePadding sx={{ width: "100%" }}>
						{matchingInsumos.map((insumo) => (
							<ListItem key={insumo.id} disablePadding>
								<ListItemButton onClick={() => setSelectedInsumo(insumo)}>
									<ListItemText
										primary={`${insumo.name} (${insumo.provider_name})`}
									/>
								</ListItemButton>
							</ListItem>
						))}
					</List>
				) : (
					<Typography variant="body2" color="text.secondary">
						Nenhum insumo encontrado para esse termo.
					</Typography>
				))}

			{selectedInsumo && (
				<>
					<Typography variant="subtitle1" sx={{ marginTop: 2 }}>
						Combos que usam "
						<strong>
							{selectedInsumo.name} ({selectedInsumo.provider_name})
						</strong>
						"
					</Typography>
					{matchingProducts.length > 0 ? (
						<List dense disablePadding sx={{ width: "100%" }}>
							{matchingProducts.map((product) => (
								<ListItem key={product.id} disablePadding>
									<ListItemButton
										onClick={() => navigate(`/product/${product.id}`)}
									>
										<ListItemText primary={product.name} />
									</ListItemButton>
								</ListItem>
							))}
						</List>
					) : (
						<Typography variant="body2" color="text.secondary">
							Não existem combos relacionados a esse insumo.
						</Typography>
					)}
				</>
			)}

			<Divider sx={{ marginY: 4 }} />

			<Typography variant="h6">Produtos</Typography>
			<Typography
				variant="body2"
				color="text.secondary"
				sx={{ marginBottom: 2 }}
			>
				Visualize e pesquise os produtos (combos) cadastrados.
			</Typography>

			<List dense disablePadding sx={{ width: "100%" }}>
				<ListItem key="search" disablePadding>
					<FormControl fullWidth sx={{ m: 1 }} variant="standard">
						<InputLabel htmlFor="standard-adornment-amount">
							Pesquisar
						</InputLabel>
						<Input
							id="input-search"
							startAdornment={
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							}
							onChange={handleChangeSearch}
						/>
					</FormControl>
				</ListItem>

				{loading ? (
					<ListItem disablePadding>
						<ListItemAvatar>
							<CircularProgress size={24} />
						</ListItemAvatar>
						<ListItemText primary="Carregando produtos…" />
					</ListItem>
				) : (
					products?.map((item) => (
						<ListItem
							key={item.id}
							disablePadding
							secondaryAction={
								<IconButton
									edge="end"
									aria-label="delete"
									onClick={() => {
										handleModalOpen(item.id);
									}}
								>
									<DeleteIcon />
								</IconButton>
							}
						>
							<ListItemButton onClick={() => navigate(`/product/${item.id}`)}>
								<ListItemAvatar>
									<Avatar>
										<AccountCircleIcon />
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary={
										item.is_default_web ? (
											<>
												{item.name}{" "}
												<Chip
													label="Pacote Web Padrão"
													size="small"
													color="primary"
												/>
											</>
										) : (
											item.name
										)
									}
								/>
							</ListItemButton>
						</ListItem>
					))
				)}
			</List>
			<Paginator
				count={count}
				pageNumber={pageNumber}
				onChange={handleChangePage}
			/>
		</>
	);
}
